import styled from 'styled-components';
import bottle from '../assets/black-bottle-back.png';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IAppState } from '../store/store';
import { currencyFormat, showCurrency } from '../utility/currencyView';
import { FaMapMarkerAlt } from 'react-icons/fa';

interface Product {
  ID: number;
  uuid: string;
  product_title: string;
  location: string;
  yourself_price: number;
  yourself_price_ngn: number;
  yourself_price_zar: number;
  friends_price: number;
  friends_price_ngn: number;
  friends_price_zar: number;
  currrency: string;
}

interface ProductCardProps {
  product: Product;
  productImage?: string;
}

export const ProductCard = ({ product, productImage }: ProductCardProps) => {
  const currencyView = useSelector((state: IAppState) =>
    showCurrency(state.shared.currency)
  );
  const currency = useSelector((state: IAppState) => state.shared.currency);

  const imageUrl = productImage?.includes('cloudinary.com')
    ? productImage.replace(
        '/upload/',
        '/upload/w_791,h_624,q_auto:best,f_auto,ar_1:1,c_fit,dpr_auto,c_lpad/'
      )
    : productImage || bottle;

  const getYourselfPrice = () => {
    return currency === 'GHS'
      ? product.yourself_price
      : currency === 'ZAR'
      ? product.yourself_price_zar
      : product.yourself_price_ngn;
  };

  const getFriendsPrice = () => {
    return currency === 'GHS'
      ? product.friends_price
      : currency === 'ZAR'
      ? product.friends_price_zar
      : product.friends_price_ngn;
  };

  return (
    <CardWrapper>
      <Link to={`/products/${product.uuid}`} className="card-link">
        <div className="image-container">
          <img
            src={imageUrl}
            alt={product.product_title}
            className="product-image"
          />
        </div>
        
        <div className="content-container">
          <div className="title-section">
            <h3 className="product-title">{product.product_title}</h3>
            <div className="location">
              <FaMapMarkerAlt className="location-icon" />
              <span>{product.location}</span>
            </div>
          </div>

          <div className="divider"></div>
          
          <div className="pricing-section">
            <div className="price-option yourself">
              <div className="price-value">
                {currencyView}
                {currencyFormat(getYourselfPrice())}
              </div>
              <div className="price-label">Buy Yourself</div>
            </div>
            
            <div className="price-option friends">
              <div className="price-value">
                {currencyView}
                {currencyFormat(getFriendsPrice())}
              </div>
              <div className="price-label">Buy with Friends</div>
            </div>
          </div>
        </div>
      </Link>
    </CardWrapper>
  );
};

const CardWrapper = styled.article`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  }
  
  .card-link {
    text-decoration: none;
    color: inherit;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  .image-container {
    position: relative;
    overflow: hidden;
    aspect-ratio: 4/3;
  }
  
  .product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover .product-image {
    transform: scale(1.05);
  }
  
  .content-container {
    padding: 20px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
  
  .title-section {
    margin-bottom: 16px;
  }
  
  .product-title {
    color: #0E203F;
    font-weight: 700;
    font-size: 18px;
    margin-bottom: 8px;
    line-height: 1.3;
  }
  
  .location {
    display: flex;
    align-items: center;
    color: #6B7280;
    font-size: 14px;
  }
  
  .location-icon {
    margin-right: 6px;
    color: #53CBF4;
  }
  
  .divider {
    height: 1px;
    background: #E5E7EB;
    margin: 0 0 16px;
  }
  
  .pricing-section {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: auto;
  }
  
  .price-option {
    flex: 1;
    text-align: center;
    padding: 4px;
    border-radius: 8px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  
  .price-option:hover {
    
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  .price-value {
    font-weight: 700;
    font-size: 18px;
    margin-bottom: 4px;
  }
  
  .price-label {
    font-size: 14px;
    font-weight: 500;
  }
  
  .yourself {
    background: #F5F8FC;
    color: #0E203F;
    border-left: 3px solid #0E203F;
  }
  
  .yourself .price-value {
    color: #0E203F;
  }
  
  .yourself .price-label {
    color: #4B5563;
  }
  
  .friends {
    background: #F0FBFF;
    color: #084157;
    border-left: 3px solid #53CBF4;
  }
  
  .friends .price-value {
    color: #084157;
  }
  
  .friends .price-label {
    color: #4B5563;
  }
`;